<template>
    <div>
        <!-- Logo/Mobile Menu Button -->
        <div class="flex items-center gap-4" >
            <button v-show="!isMobileMenuOpen" class="bg-3a-nav-btn" @click="toggleMobileMenu" :title="$t('common.sidebar.open_sidebar')" 
                style="z-index:51" >
              <UIcon  name="i-heroicons-bars-3" class="h-6 w-6"  />
              
            </button>
            <span v-show="isMobileMenuOpen" class="h-4 w-8" ></span>
            <LogoIcon class="h-8 w-auto text-white z-1000 shrink-0 " style="cursor:pointer" @click="router.push('/')"/>
        </div>

        <!-- Desktop Navigation -->
        <UNavigationMenu 
            v-bind="attrs"
            :items="itemsDesktop"
            class="main-container hidden md:flex fixed left-0 top-0 shadow z-50 w-full justify-center h-12 " 
            style="font-size:12px !important"
            :ui="{
                viewport: 'sm:w-(--reka-navigation-menu-viewport-width)',
                content: 'sm:w-auto',
                childList: 'sm:w-180',
                childLinkDescription: 'text-balance line-clamp-2'
            }">
            <template #item-content="{ item, index }">
                <ul v-if="item.children && item.id != 'home'"
                    class="isolate grid grid-cols-2 gap-1 p-0 my-0 "
                    :style="{ cursor: 'pointer', width: getColWidth(index) + 'px', position: 'relative' }">
                    <li v-for="child in item.children" :key="child.label" :class="getColSpan(index)">
                    <button type="button" @click="item.id=='nombreUsuario' ? logout():router.push(child.to)"
                    style="cursor:pointer"
                            class="  w-full text-sm flex items-left justify-between rounded-md p-3 transition-colors hover:bg-elevated/50">
                        <div class="flex items-left gap-1">
                        <UIcon v-if="child.icon" :name="child.icon"
                                class="text-highlighted h-full" style="vertical-align:middle; margin-top:4px"/>
                        <div class="items-left">
                            <p class="font-medium text-highlighted text-left">{{ child.label }}</p>
                            <p class="text-muted line-clamp-2 text-left text-xs">{{ child.description }}</p>
                        </div>
                        </div>
                    </button>
                    </li>
                </ul>
            </template>

        </UNavigationMenu>

        <!-- Mobile Menu (shown when toggled) -->
        <div v-if="isMobileMenuOpen" class="fixed inset-0 sm:backdrop-blur-xs md:backdrop-filter-none lg:backdrop-filter-none bg-opacity-50 z-40 " @click="toggleMobileMenu"></div>
        
        <div v-if="isMobileMenuOpen" class="fixed top-0 left-0 h-full w-95  shadow-lg z-50 transform transition-transform flex flex-col"
          :style="menuStyle == '1' ? '-webkit-backdrop-filter: blur(10px); backdrop-filter: blur(8px); background: linear-gradient(147.04deg, rgb(0 75 119 / 80%) 0.74%, #17185f 99.61%)':
          'backdrop-filter: blur(8px); background: linear-gradient(147.04deg, rgb(0 0 0 / 32%) 0.74%, rgba(38, 39, 114, 0.26) 99.61%)'">
            <div class="p-2 flex justify-end " :title="$t('common.sidebar.close_sidebar')">
                <button class="bg-3a-nav-btn" @click="toggleMobileMenu" style="cursor:pointer">
                    <UIcon name="i-heroicons-x-mark" class="h-6 w-6" />
                </button>
            </div>
            <div class="px-4 pt-2 flex items-start pb-4 flex-shrink-0">
              <span class="text-white font-medium">
              {{ nombreUsuario }} <br />
              <small class="text-white/80">{{ email }}</small>
              </span>
            </div>
            <!-- Body (scrollable menu) -->
            <div class="menux px-4 py-2 flex-1 overflow-y-auto">
              <!-- 🔍 Search input -->
              <div class="relative mb-2">
                <input
                  v-model="search"
                  type="text"
                  :placeholder="$t('common.sidebar.search')" 
                  class="w-full rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/50 pl-3 pr-9 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <Search class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
              </div>
              <UNavigationMenu
                  :items="filteredItems"
                  orientation="vertical"
                  class="data-[orientation=vertical] text-white"
                  :ui="{
                      childList: 'w-full',
                      childLinkDescription: 'text-balance line-clamp-2'
                  }"
              >
              </UNavigationMenu>
            </div>
             <!-- Footer flex -->
            <div class=" items-center justify-between border-b border-accented px-6 pb-2.5 ">
              <div class="p-0 flex-shrink-0 mb-0">
                  <div class="flex flex-col items-center justify-center w-full text-white hover:text-gray-200 transition text-xs">
                      <LanguageSwitcher />
                  </div>
              </div> 
              <div class="p-4 flex items-center justify-center w-full">
                  <button @click="logout" class="bg-3a-nav-btn flex flex-col items-center justify-center text-white hover:text-gray-200 transition">
                      <UIcon name="i-lucide-log-out" class="h-6 w-6 mb-1" />
                      <span class="text-sm">{{$t('common.sidebar.logout') }}</span>
                  </button>
              </div>
            </div>
        </div>
    </div>
</template> 
<script setup lang="ts">
const { t: $t } = useI18n()

import type { NavigationMenuItem } from '@nuxt/ui'
import { useAttrs } from 'vue'
import LogoIcon from '~/assets/3a-logo-core-materials.svg'
import { ref } from 'vue';
import { useUserStore } from '~/stores/user'
import { Search } from 'lucide-vue-next'

const router = useRouter()

const props = defineProps<{
  menuStyle?: string,
  navbarStyle?: string
}>();

const { menuStyle, navbarStyle } = props
const { locale } = useI18n()
const items = ref<NavigationMenuItem[]>([])
const itemsDesktop = ref<NavigationMenuItem[]>([]);
const search = ref('')

// función recursiva para filtrar
function filterNodes(nodes: any[], term: string): any[] {
  return nodes
    .map(node => {
      const match = node.label.toLowerCase().includes(term.toLowerCase())
      const children = node.children ? filterNodes(node.children, term) : []

      // incluye el nodo si coincide o si alguno de sus hijos coincide
      if (match || children.length > 0) {
        return { ...node, children }
      }
      return null
    })
    .filter(Boolean)
}

// computed reactivo para el resultado
const filteredItems = computed(() => {
  if (!search.value) return items.value
  return filterNodes(items.value, search.value)
})

const isMobileMenuOpen = ref(false);
const nombreUsuario = JSON.parse(localStorage.getItem("rla-auth")|| "[{}]").user.nombre || 'usuario';
const rolUsuario = JSON.parse(localStorage.getItem("rla-auth")|| "[{}]").user.rol || 'Usuario del sistema';
const email = JSON.parse(localStorage.getItem("rla-auth")|| "[{}]").user.email || '';
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const attrs = useAttrs();

watchEffect(async () => {
    buildMenu();
})
onMounted(() => {  
    buildMenu();
});

async function buildMenu()
{
    let menuItems = await useMenuItems(locale.value);
    items.value = menuItems;
    itemsDesktop.value = [...items.value] as NavigationMenuItem[] ;
    const isDesktop = window.matchMedia('(min-width: 900px)').matches;

    const mq = window.matchMedia('(min-width: 900px)');
    const apply = () => {
      if (mq.matches) {
        // agregar si no existe
        if (!itemsDesktop.value.some(i => i.id === "nombreUsuario")) {
          itemsDesktop.value.push({
            label: nombreUsuario,
            icon: 'i-lucide-circle-user',
            id: 'nombreUsuario',
            orientation: 'vertical',
            children: [
              {
                label: $t('common.top_navbar.logout_menu'),
                icon: 'i-lucide-log-out',
                orientation: 'vertical',
                description: $t('common.top_navbar.logout_menu_subtitle'),
                onSelect: () => logout(),
                to: "/"
              }
            ]
          });
        }
      } else {
        // remover si baja de 900px
        itemsDesktop.value = itemsDesktop.value.filter(i => i.id !== 'nombreUsuario');
      }
    }
    mq.addEventListener('change', apply);
    apply();
}

const emit = defineEmits<{
  (e: 'select', item: NavigationMenuItem): void
}>()

async function logout(){
    const authToken = useCookie('rla-token')
    authToken.value = null
    useUserStore().logout()
    await nextTick();
    navigateTo('/login') // or use `useRouter().push('/login')`
}
const getColSpan = (i: number) => {
  // indexes 0 and 1 span full width (1 column), rest span half width (2 per row)
  switch(i){
    case 0 : case 1 : case 3 :  return 'col-span-1';
    case 2 : return 'col-span-1';
    default: return 'col-span-1';
    
  } 
};
const getColWidth = (i: number) => {
  // indexes 0 and 1 span full width (1 column), rest span half width (2 per row)
  switch(i){
    case 0 : case 1 : case 3 :  return '600';
    case 2 : return '600';
    default: return '600';
    
  } 
};
</script>
<style >
.menux {
 scrollbar-width: thin;
 scrollbar-color: #7283a1 #f3f4f600;
}
.menux::-webkit-scrollbar {
  width: 6px;
}
.menux::-webkit-scrollbar-track {
  background: #f3f4f6;
}
.menux::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 9999px;
}
nav > ul > li > a.text-muted,
nav > ul > li > button.text-muted,
nav > ul > li > button > span {
  color: white !important;
}

nav > div > ul > li > a.text-muted,
nav > div > ul > li > button.text-muted,
nav > div > ul > li > button > span {
  color: white !important;
}

div.vertical-navbar-items > nav > ul > li > div > ul > li > a.text-muted,
div.vertical-navbar-items > nav > ul > li > div > ul > li > a.text-muted,
div.vertical-navbar-items > nav > ul > li > div > ul > li > button.text-muted,
div.vertical-navbar-items > nav > ul > li > div > ul > li > button > span ,
div.vertical-navbar-items > nav > ul > li > div > ul > li > button > a > span,
nav > ul > li > a > span.iconify,
nav > ul > li > div > ul > li > a > span{
  color: white !important;
}

nav > ul > li > a.text-primary>span.iconify,
nav > ul > li > a.text-primary>span>span.iconify,
nav > ul > li > div > ul > li > a.text-primary>span.iconify  {
    color: rgba(0, 119, 188, 1) !important;
}

nav > ul > li > div > ul > li  {
    padding-right: 20px;
}
nav > ul > li > a.text-primary>span.truncate,
nav > ul > li > div > ul > li > a.text-primary>span.truncate  {
    background: linear-gradient(180deg,rgba(0, 119, 188, 1) 0%, rgba(38, 39, 114, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent; /* fallback */
}

div.vertical-navbar-items > nav > ul > li > div > ul > li > a > span.iconify  {
  color: white !important;
}

div.vertical-navbar-items a.text-primary {
  width: 90% ;
}
div.vertical-navbar-items a.text-primary>span.truncate {
  background: linear-gradient(180deg,rgba(0, 119, 188, 1) 0%, rgba(38, 39, 114, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent; /* fallback */
}

div.vertical-navbar-items a.text-primary>span.iconify  {
    color: rgba(38, 39, 114, 1) !important;
}

nav > div > ul > li > a.text-primary>span.iconify  {
    color: rgba(38, 39, 114, 1) !important;
}


nav > div > ul > li > a > span.i-lucide\:home {
  background-color: white !important;
}
nav > div > div > div > ul > li > a > span {
  margin-top:3px /* alínea los iconos en los submenús */
}
nav > div > ul > li > a > span {
  color: white !important;
}
nav > div > ul > li > a.text-primary {
  background: linear-gradient(180deg,rgba(0, 119, 188, 1) 0%, rgba(38, 39, 114, 1) 68%);-webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;  /*fallback */
}
nav > div > ul > li > a.text-primary > span.i-lucide\:home {
  background-color: rgba(38, 39, 114, 1) !important;
}

.font-semibold-custom{
    font-weight: 600;
    background: linear-gradient(180deg,rgba(0, 119, 188, 1) 0%, rgba(38, 39, 114, 1) 68%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;  /*fallback */

}
nav.navbar > div > ul > li > a.text-primary > span.iconify,
nav.navbar > div > ul > li > a.text-primary > span,
nav.navbar > div >div >div > ul > li > a > span,
nav.navbar > div >div >div > ul > li > a,
nav.navbar > div >div >div > ul > li > button > span  {
    color: rgba(38, 39, 114, 1);
}
@media (min-width: 1024px) {
    .main-container {
        display: flex;
        justify-content: center; /* default */
    }
}

@media (max-width: 1023px) {
    .main-container {
        justify-content: right !important;
        padding-right: 30px;
    }
}
</style>